// @flow

/* -----------------Globals--------------- */
import produce from 'immer';

/* -----------------Types--------------- */
import {
  type FolderState,
  type PartialContent,
} from './types';
import type {
  SortingTypes,
  SortItem,
} from '../types';
/* -----------------Actions--------------- */
import {
  SELECT_ONE,
  DESELECT_ONE,
  SELECT_ALL,
  DESELECT_ALL,
  TOGGLE_REORDER,
  SORT_TYPE,
  CURRENT_FOLDER,
} from './actionTypes';

import {
  initialState,
} from './constants';

/* -------------------Handlers----------------------- */
const handleSelectOne = (
  state: FolderState,
  data: *,
) => produce(state, (draft) => {
  const {
    selectedContentItems,
  } = state;
  const {
    id,
    meta,
    content,
  } = data;
  selectedContentItems.set(id, {
    metadata: meta,
    content,
  });
  draft.selectedContentItems = new Map([...selectedContentItems]);
});

const handleDeselectOne = (
  state: FolderState,
  id: string,
) => produce(state, (draft) => {
  const {
    selectedContentItems,
  } = state;
  selectedContentItems.delete(id);
  draft.selectedContentItems = new Map([...selectedContentItems]);
});

const handleDeselectAllAndSelectAll = (
  state: FolderState,
  deSelect: boolean,
  contentData: ?PartialContent[],
) => produce(state, (draft) => {
  const {
    selectedContentItems,
  } = state;

  if (deSelect && selectedContentItems && selectedContentItems.size > 0) {
    draft.selectedContentItems = new Map([]);
  } else if (contentData) {
    contentData.forEach(
      ({
        id,
        content,
        metadata,
      }) => {
        selectedContentItems.set(id, {
          metadata,
          content,
        });
      },
    );
    draft.selectedContentItems = new Map([...selectedContentItems]);
  }
});

const handleReordering = (
  state: FolderState,
) => produce(state, (draft) => {
  const { inReorderingMode } = state;
  draft.inReorderingMode = !inReorderingMode;
});

const evaluateSortItem = (
  currentSortType: SortItem,
  newSortType: SortingTypes,
) => {
  const finalSortType = { name: newSortType, isAscending: true };
  if (currentSortType.name === newSortType) {
    finalSortType.isAscending = !currentSortType.isAscending;
  }
  return finalSortType;
};

const handleSetSortType = (
  state: FolderState,
  sortType: SortingTypes,
) => produce(state, (draft) => {
  const {
    currentSortType,
  } = state;
  const newSortItem = evaluateSortItem(currentSortType, sortType);
  draft.currentSortType = newSortItem;
});

const handleCurrentFolder = (
  state: FolderState,
  data: *,
) => produce(state, (draft) => {
  draft.currentFolder = data;
});

const folderReducer = (
  state: FolderState = initialState,
  action: *,
) => {
  switch (action.type) {
    case SELECT_ONE:
      return handleSelectOne(
        state,
        action.data,
      );
    case DESELECT_ONE:
      return handleDeselectOne(
        state,
        action.data,
      );
    case SELECT_ALL:
      return handleDeselectAllAndSelectAll(
        state,
        false,
        action.data,
      );
    case DESELECT_ALL:
      return handleDeselectAllAndSelectAll(
        state,
        true,
      );
    case TOGGLE_REORDER:
      return handleReordering(
        state,
      );
    case SORT_TYPE:
      return handleSetSortType(
        state,
        action.data,
      );
    case CURRENT_FOLDER:
      return handleCurrentFolder(
        state,
        action.data,
      );
    default:
      return state;
  }
};

export default folderReducer;
